/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { chatSummarize } from "../funcs/chatSummarize.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SummarizeChatResult } from "../models/components/summarizechatresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SummarizeChatRequest,
  SummarizeChatSecurity,
} from "../models/operations/summarizechat.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SummarizeChatMutationVariables = {
  request: SummarizeChatRequest;
  security?: SummarizeChatSecurity | undefined;
  options?: RequestOptions;
};

export type SummarizeChatMutationData = SummarizeChatResult;

export type SummarizeChatMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * summarize chat
 *
 * @remarks
 * Generate or return a persisted LLM summary of a chat session transcript. When a summary already exists and regenerate is false, returns the cached summary without calling the model.
 */
export function useSummarizeChatMutation(
  options?: MutationHookOptions<
    SummarizeChatMutationData,
    SummarizeChatMutationError,
    SummarizeChatMutationVariables
  >,
): UseMutationResult<
  SummarizeChatMutationData,
  SummarizeChatMutationError,
  SummarizeChatMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSummarizeChatMutation(client, options),
    ...options,
  });
}

export function mutationKeySummarizeChat(): MutationKey {
  return ["@gram/client", "chat", "summarize"];
}

export function buildSummarizeChatMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SummarizeChatMutationVariables,
  ) => Promise<SummarizeChatMutationData>;
} {
  return {
    mutationKey: mutationKeySummarizeChat(),
    mutationFn: function summarizeChatMutationFn({
      request,
      security,
      options,
    }): Promise<SummarizeChatMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(chatSummarize(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
